import React, { Ref, RefObject, useContext, useEffect } from 'react';
import { Node } from '@react-types/shared';
import { AriaButtonProps } from 'react-aria/useButton';
import { AriaTabProps, useTab } from 'react-aria/useTabList';
import { TabListState } from 'react-stately/useTabListState';
import { SapphireStyleProps } from '../../../utils';
import { UnderlinedTabBase } from './UnderlinedTabBase';
import {
  TabElementType,
  TabsContext,
  TabsContextValue,
  useIntersectionObserver,
} from './UnderlinedTabsList';
import { AriaLabelingPropsKeys } from '../../../utils/types';

export interface UnderlinedTabProps
  extends Omit<
      AriaButtonProps<'div'>,
      'href' | 'target' | 'rel' | 'elementType' | 'children'
    >,
    SapphireStyleProps,
    Pick<AriaTabProps, AriaLabelingPropsKeys> {
  item: Node<object>;
  state: TabListState<object>;
}

export function UnderlinedTab({
  item: { key, rendered },
  state,
}: UnderlinedTabProps): JSX.Element {
  const contextValue = useContext<TabsContextValue | null>(TabsContext);

  if (!contextValue) {
    throw new Error('Component must be wrapped with <TabsContext.Provider>');
  }
  const tabRef = React.useRef<TabElementType>(null);
  const {
    registerTab,
    deregisterTab,
    setActiveTabRef,
    intersectionObserver,
    scrollTabIntoView,
  } = contextValue;

  const { tabProps, isDisabled } = useTab(
    { key, shouldSelectOnPressUp: true },
    state,
    tabRef
  );

  const isActive = state.selectedKey === key;

  useIntersectionObserver(
    tabRef as RefObject<TabElementType>,
    intersectionObserver
  );

  useEffect(() => {
    if (tabRef.current && isActive) {
      const tab = tabRef.current;
      setTimeout(() => {
        scrollTabIntoView(tab, false);
      }, 0);
    }
  }, [tabRef.current]);

  useEffect(() => {
    if (tabRef.current && isActive) {
      scrollTabIntoView(tabRef.current);
      setActiveTabRef(tabRef);
    }
  }, [isActive, tabRef.current]);

  useEffect(() => {
    if (tabRef.current && registerTab) {
      registerTab(tabRef);
    }

    return () => deregisterTab(tabRef);
  }, [tabRef]);

  return (
    <UnderlinedTabBase
      {...tabProps}
      elementType="div"
      isActive={isActive}
      isDisabled={isDisabled}
      ref={tabRef as Ref<TabElementType>}
    >
      {rendered}
    </UnderlinedTabBase>
  );
}
